// src/services/rewardsService.js
//
// STEP 5 — the browser side of the daily treasure trail, chests and sticker
// albums. Everything that gives something away runs in functions/rewards.js;
// this file only asks, and listens.
//
//   todayKey()               "YYYY-MM-DD" in New York time (same day as the server)
//   trailStatus(trail)       { canClaim, nextStep, world, stepsDone }
//   claimDailyTrail()        → { trail, coins, chestId } | { already }
//   useChests()              { chests: [unopened…], loaded } — live
//   openChest(chestId)       → { coins, powerup, stickers, item, … }
//   useCollection()          { stickers: {id: count}, albums: {id: true}, loaded }
//   claimAlbum(albumId)      → { coins, item }
//   giveClassChest(uids)     teacher → { given, notInClass }

import { useEffect, useState } from "react";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/firebaseConfig";
import DATA from "../components/rewards/rewardsData.json";

export const REWARDS = DATA;

export const todayKey = (date = new Date()) => date.toLocaleDateString("en-CA", { timeZone: "America/New_York" });

export const trailStatus = (trail) => {
  const t = { step: 0, world: 0, lastDay: "", ...(trail || {}) };
  const canClaim = t.lastDay !== todayKey();
  const restart = canClaim && t.step >= DATA.trail.chestDay;
  return {
    canClaim,
    // what the map shows: a finished trail stays finished until the new world starts
    stepsDone: restart ? 0 : t.step,
    nextStep: restart ? 1 : t.step + 1,
    world: restart ? t.world + 1 : t.world,
  };
};

const call = async (name, data = {}) => {
  const res = await httpsCallable(functions, name)(data);
  return res?.data || null;
};

export const claimDailyTrail = () => call("claimDailyTrail");
export const openChest = (chestId) => call("openChest", { chestId });
export const claimAlbum = (albumId) => call("claimAlbum", { albumId });
export const giveClassChest = (studentUids) => call("giveClassChest", { studentUids });

/** Live list of the signed-in student's unopened chests (oldest first). */
export const useChests = () => {
  const [state, setState] = useState({ chests: [], loaded: false });
  useEffect(() => {
    let stop = () => {};
    const stopAuth = onAuthStateChanged(getAuth(), (user) => {
      stop();
      if (!user) {
        setState({ chests: [], loaded: true });
        return;
      }
      stop = onSnapshot(
        collection(db, "wallets", user.uid, "chests"),
        (snap) => {
          const chests = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((c) => !c.opened)
            .sort((a, b) => (a.at?.seconds || 0) - (b.at?.seconds || 0));
          setState({ chests, loaded: true });
        },
        () => setState({ chests: [], loaded: true })
      );
    });
    const onGot = (e) => {
      const id = e.detail?.chestId;
      if (id) setState((p) => ({ ...p, chests: p.chests.filter((c) => c.id !== id) }));
    };
    window.addEventListener("sbl:rewards-changed", onGot);
    return () => {
      stop();
      stopAuth();
      window.removeEventListener("sbl:rewards-changed", onGot);
    };
  }, []);
  return state;
};

/** Live sticker collection. */
export const useCollection = () => {
  const [state, setState] = useState({ stickers: {}, albums: {}, loaded: false });
  useEffect(() => {
    let stop = () => {};
    const stopAuth = onAuthStateChanged(getAuth(), (user) => {
      stop();
      if (!user) {
        setState({ stickers: {}, albums: {}, loaded: true });
        return;
      }
      stop = onSnapshot(
        doc(db, "wallets", user.uid, "collection", "main"),
        (snap) => {
          const d = (snap.exists() && snap.data()) || {};
          setState({ stickers: d.stickers || {}, albums: d.albums || {}, loaded: true });
        },
        () => setState((p) => ({ ...p, loaded: true }))
      );
    });
    // a chest was just opened: put its stickers in the album NOW (the
    // database copy follows a moment later and agrees)
    const onGot = (e) => {
      const got = e.detail?.stickers || [];
      if (!got.length) return;
      setState((p) => {
        const stickers = { ...p.stickers };
        got.forEach((st) => {
          if (st?.id) stickers[st.id] = Math.max(1, (Number(stickers[st.id]) || 0) + (st.isNew ? 0 : 1));
        });
        return { ...p, stickers };
      });
    };
    window.addEventListener("sbl:rewards-changed", onGot);
    return () => {
      stop();
      stopAuth();
      window.removeEventListener("sbl:rewards-changed", onGot);
    };
  }, []);
  return state;
};

/** How many stickers of an album are found. */
export const albumProgress = (album, stickers) => album.stickers.filter((s) => (stickers[s.id] || 0) > 0).length;
